<template>
  <main id="main-app" class="tw-flex tw-flex-col tw-bg-white tw-h-full">
    <router-view />
  </main>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { computed, onMounted, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import EventBus, { PatientChatEvents, VideoCallEvents } from '@shared/event-bus'
import { ViewerMessageType } from '@shared/models/enums/web-viewer'
import { useUserLocale } from '@shared/utils/composables/locales'
import { isIframePage } from '@shared/utils/helpers/is-iframe-page'

import { ChatWidget } from '@protos/apps/pc/chat_widget_pb'

import { useChatSetup } from '@pc/composables/auth-guards'
import { createConversation } from '@pc/composables/create-conversation'
import routeNames from '@pc/router/route-names'
import { useConversationsStore } from '@pc/store/conversations'
import { useDemoModeStore } from '@pc/store/demo'
import { useSessionStore } from '@pc/store/session'
import { useSocketStore } from '@pc/store/socket'

type Route = {
  name?: string
  query?: Record<string, string | number | null | string[]>
  params: Record<string, string | number | null | string[]>
}

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const demoModeStore = useDemoModeStore()
const sessionStore = useSessionStore()

const socketStore = useSocketStore()

const conversationStore = useConversationsStore()

const { userLocale } = useUserLocale()

const { ensureIsWidgetTouched } = useChatSetup(userLocale)

const widget = computed(() => sessionStore.chatProps?.chatWidget)
const routeConversationId = computed(() => Number(route.params.conversationId))

EventBus.on(VideoCallEvents.RedirectToConversation, () => {
  router.replace({ name: routeNames.conversation })
})

const isPdfLoaded = (event: MessageEvent) => {
  return (
    event.data?.type === 'requestConfig' ||
    event.data?.type === ViewerMessageType.VIEWER_LOADED ||
    (event.isTrusted && (typeof event.data === 'object' || event.data === ''))
  )
}

const addMessageListener = () => {
  window.addEventListener('message', async (e) => {
    conversationStore.isIframePage = isIframePage()

    if (!demoModeStore.isChatOpened && e.data?._globals?.isOpen) {
      demoModeStore.isChatOpened = true
      /**
       * @description
       * If the chat is opened from the embed widget, we need to create a new conversation only after user open widget
       * and redirect the user to the conversation page
       */
      try {
        if (
          !routeConversationId.value &&
          router.currentRoute.value.name !== routeNames.landingPage
        ) {
          const data = (await createConversation()) as Route

          if (data?.params) {
            router.push({
              name: routeNames.conversation,
              params: { ...data.params },
            })
          }
        }
      } finally {
        window.parent.postMessage('open', '*')
      }
    }

    if (
      demoModeStore.isChatOpened &&
      !e.data?._globals?.isOpen &&
      !isPdfLoaded(e as MessageEvent)
    ) {
      demoModeStore.isChatOpened = false
      window.parent.postMessage('close', '*')
    }
  })
}

const onUnloadListener = () => {
  addEventListener('unload', () => {
    socketStore.closeSocketConnection()
  })
}

watch(locale, (value) => dayjs.locale(value.split('-')[0]))

watch(
  () => demoModeStore.isChatOpened,
  () => {
    if (!widget.value) {
      return
    }

    demoModeStore.updateDemoChatButton(widget.value as ChatWidget)
  }
)

watch(
  () => widget.value?.id,
  (chatWidgetId) => {
    if (chatWidgetId) {
      ensureIsWidgetTouched()
    }
  },
  {
    immediate: true,
  }
)

watchEffect(() => {
  //TODO socket connection logic is already implemented in conversation page
  if (
    sessionStore.authSession &&
    route.name &&
    route.name !== routeNames.conversation &&
    route.name !== routeNames.quickFormPage
  ) {
    socketStore.createSocketConnection()
  }
})

onMounted(() => {
  addMessageListener()

  onUnloadListener()
})

EventBus.on(PatientChatEvents.redirectToTac, () => {
  const currentRouteName = (router?.currentRoute?.value?.name ?? '').toString()

  if (currentRouteName && routeNames.tac !== currentRouteName) {
    router.replace({
      name: routeNames.tac,
      query: {
        to: currentRouteName,
        conversationId: router?.currentRoute?.value.params.conversationId,
      },
    })
  }
})
</script>
<style lang="scss">
$patient-header-buttons-widht: 100px;

body {
  scroll-behavior: smooth;
}

#patient-chat-app {
  .fullscreen-dialog {
    max-width: $max-patient-width !important;

    &--text {
      max-width: calc(
        $max-patient-width - $patient-header-buttons-widht
      ) !important;
    }
  }
}
</style>
